"use client";

import { useEffect, useState } from "react";
import { REFERENCE_PX } from "@/lib/text-metrics";

/**
 * True once `fontFamily` at `weight` has actually finished loading in the
 * browser. Canvas measureText() silently uses a fallback font until the
 * real one is loaded, so components must wait for this before trusting
 * any measurement.
 *
 * Implemented as a key comparison (rather than an explicit "reset to
 * false") so the returned value flips to false the instant the font
 * changes, without ever calling setState synchronously inside the effect.
 */
export function useFontReady(fontFamily: string, weight: number): boolean {
  const key = `${weight}|${fontFamily}`;
  const [readyKey, setReadyKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const markReady = () => {
      if (!cancelled) setReadyKey(key);
    };

    const hasFontLoadingApi =
      typeof document !== "undefined" && "fonts" in document;

    if (!hasFontLoadingApi) {
      Promise.resolve().then(markReady);
    } else {
      document.fonts
        .load(`${weight} ${REFERENCE_PX}px ${fontFamily}`)
        .then(() => document.fonts.ready)
        .then(markReady)
        .catch(markReady);
    }

    return () => {
      cancelled = true;
    };
  }, [fontFamily, weight, key]);

  return readyKey === key;
}
